import { useCallback, useMemo, useState } from "react";
import {
  mergeTablePreferences,
  readTablePreferences,
  writeTablePreferences,
  type TablePreferences,
} from "./tablePreferencesStorage";

export interface TableColumnPreferenceMeta {
  id: string;
  label: string;
  alwaysVisible?: boolean;
  defaultHidden?: boolean;
}

interface PreferenceColumnSource {
  id: string;
  header: string;
  alwaysVisible?: boolean;
  defaultHidden?: boolean;
}

export function toPreferenceColumns(
  columns: PreferenceColumnSource[],
): TableColumnPreferenceMeta[] {
  return columns.map((column) => ({
    id: column.id,
    label: column.header,
    alwaysVisible: column.alwaysVisible,
    defaultHidden: column.defaultHidden,
  }));
}

interface UseTablePreferencesOptions {
  tableId: string;
  columns: TableColumnPreferenceMeta[];
}

export function useTablePreferences({
  tableId,
  columns,
}: UseTablePreferencesOptions) {
  const columnIds = useMemo(() => columns.map((column) => column.id), [columns]);
  const alwaysVisibleColumnIds = useMemo(
    () =>
      columns.filter((column) => column.alwaysVisible).map((column) => column.id),
    [columns],
  );
  const defaultHiddenColumnIds = useMemo(
    () =>
      columns
        .filter((column) => column.defaultHidden && !column.alwaysVisible)
        .map((column) => column.id),
    [columns],
  );

  const [preferences, setPreferences] = useState<TablePreferences>(() =>
    mergeTablePreferences(
      readTablePreferences(tableId),
      columnIds,
      alwaysVisibleColumnIds,
      defaultHiddenColumnIds,
    ),
  );

  const resolved = useMemo(
    () =>
      mergeTablePreferences(
        preferences,
        columnIds,
        alwaysVisibleColumnIds,
        defaultHiddenColumnIds,
      ),
    [alwaysVisibleColumnIds, columnIds, defaultHiddenColumnIds, preferences],
  );

  const applyPreferences = useCallback(
    (next: TablePreferences) => {
      const merged = mergeTablePreferences(
        next,
        columnIds,
        alwaysVisibleColumnIds,
        defaultHiddenColumnIds,
      );
      setPreferences(merged);
      writeTablePreferences(tableId, merged);
    },
    [alwaysVisibleColumnIds, columnIds, defaultHiddenColumnIds, tableId],
  );

  const resetPreferences = useCallback(() => {
    const defaults = mergeTablePreferences(
      null,
      columnIds,
      alwaysVisibleColumnIds,
      defaultHiddenColumnIds,
    );
    setPreferences(defaults);
    writeTablePreferences(tableId, defaults);
  }, [alwaysVisibleColumnIds, columnIds, defaultHiddenColumnIds, tableId]);

  const orderedVisibleColumnIds = useMemo(() => {
    const visible = new Set(resolved.visibleColumnIds);
    return resolved.columnOrder.filter((id) => visible.has(id));
  }, [resolved.columnOrder, resolved.visibleColumnIds]);

  return {
    preferences: resolved,
    pageSize: resolved.pageSize,
    orderedVisibleColumnIds,
    applyPreferences,
    resetPreferences,
  };
}
